import React, { useState, useEffect } from 'react';
import { BypassLayout } from '../components/common';
import ManagerModal from '../components/ManagerManagement/ManagerModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { PenSquare, Trash2 } from 'lucide-react';
import { Button, ConfirmModal } from '../components/common';

function ManagerManagement() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [totalPages, setTotalPages] = useState(1);
  const PER_PAGE = 20;
  const [hasMore, setHasMore] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, userId: null });

  const fetchManagers = async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager?page=${pageNum}&per_page=${PER_PAGE}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.status === 'error') {
        setManagers([]);
      } else {
        setManagers(data.data);
        setTotalPages(data.total_pages || 1);
        setHasMore(pageNum < data.total_pages);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('관리자 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, [page]);

  // page 변경 시 pageInputValue 동기화
  useEffect(() => {
    setPageInputValue(String(page));
  }, [page]);

  const handleAddManager = async (managerData) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(managerData),
      });

      if (!response.ok) throw new Error('관리자 추가 실패');
      
      await fetchManagers();
      return '관리자가 성공적으로 추가되었습니다.';
    } catch (error) {
      throw new Error('관리자 추가에 실패했습니다.');
    }
  };

  const handleEditManager = async (managerData) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(managerData),
      });

      if (!response.ok) throw new Error('관리자 정보 수정 실패');
      
      await fetchManagers();
      return '관리자 정보가 성공적으로 수정되었습니다.';
    } catch (error) {
      throw new Error('관리자 정보 수정에 실패했습니다.');
    }
  };

  const handleDeleteManager = (userId) => {
    setDeleteConfirm({ isOpen: true, userId });
  };

  const confirmDelete = async () => {
    const { userId } = deleteConfirm;
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/manager`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) throw new Error('관리자 삭제 실패');
      
      await fetchManagers();
    } catch (error) {
      console.error('관리자 삭제 실패:', error);
    } finally {
      setDeleteConfirm({ isOpen: false, userId: null });
    }
  };

  const handleModalOpen = (mode, manager = null) => {
    setModalMode(mode);
    if (mode === 'edit' && manager) {
      setSelectedManager({
        id: manager.id,
        user_id: manager.user_id,
        user_name: manager.user_name,
        charge_work: manager.charge_work
      });
    }
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedManager(null);
    setModalMode('add');
  };

  const handlePageChange = (newPage) => {
    fetchManagers(newPage);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    let end = Math.min(start + maxVisible - 1, totalPages);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    if (start > 1) {
      pageNumbers.push(
        <Button
          key={1}
          variant="secondary"
          size="sm"
          onClick={() => handlePageChange(1)}
        >
          1
        </Button>
      );
      if (start > 2) {
        pageNumbers.push(<span key="ellipsis1" className="px-2">...</span>);
      }
    }

    for (let i = start; i <= end; i++) {
      if (page === i) {
        // 현재 페이지 - 인풋 박스로 렌더링
        const inputWidth = Math.max(2.5, String(pageInputValue).length * 0.6 + 1.5);
        pageNumbers.push(
          <input
            key={i}
            type="text"
            value={pageInputValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setPageInputValue(value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newPage = parseInt(pageInputValue);
                if (newPage >= 1 && newPage <= totalPages) {
                  e.target.blur();
                  setTimeout(() => {
                    handlePageChange(newPage);
                  }, 100);
                } else {
                  setPageInputValue(String(page));
                }
              }
            }}
            onMouseUp={(e) => e.preventDefault()}
            onFocus={(e) => e.target.select()}
            onBlur={() => setPageInputValue(String(page))}
            className="flex items-center justify-center text-sm rounded bg-headerBg text-white font-bold"
            style={{ width: `${inputWidth}rem`, minWidth: '2.5rem', height: '32px', textAlign: 'center', outline: 'none', cursor: 'text', padding: '0 8px' }}
          />
        );
      } else {
        pageNumbers.push(
          <Button
            key={i}
            variant="secondary"
            size="sm"
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Button>
        );
      }
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        pageNumbers.push(<span key="ellipsis2" className="px-2">...</span>);
      }
      pageNumbers.push(
        <Button
          key={totalPages}
          variant="secondary"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return pageNumbers;
  };

  return (
    <BypassLayout
      loading={isLoading}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col ${isMobileView ? 'gap-4' : 'gap-4'} ${
            isMobileView 
              ? 'h-full overflow-y-auto' 
              : 'h-[calc(100vh-7rem)]'
          }`}
          style={getSafeAreaStyle()}
        >
          {/* 네비게이션 (데스크톱만) */}
          {!isMobileView && (
            <nav className="shrink-0">
              <div className="flex items-center text-sm text-gray-600">
                <Link to="/managers" className="hover:text-primary">관리자관리</Link>
              </div>
            </nav>
          )}

          {/* 메인 카드 컨테이너 */}
          <div className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg ${
            isMobileView ? 'flex-1' : 'flex-1'
          }`}>
            {/* 헤더 섹션 */}
            <div className="border-b bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-semibold">관리자 관리</h3>
                <Button 
                  variant="primary"
                  onClick={() => handleModalOpen('add')}
                  className="px-3 py-1 text-xs"
                  size="sm"
                >
                  관리자 추가
                </Button>
              </div>
            </div>

            {/* 관리자 목록 테이블 */}
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-x-auto">
                <table className="h-full min-w-full">
                  <thead className="sticky top-0 bg-headerBg">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">관리자 ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">이름</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">담당 업무</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">등록일</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">수정일</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-white">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {managers.map((manager) => (
                      <tr key={manager.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4 text-sm">{manager.user_id}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">{manager.user_name}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm">{manager.charge_work}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{new Date(manager.created_at).toLocaleDateString()}</td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{new Date(manager.updated_at).toLocaleDateString()}</td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost"
                              size="sm"
                              className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark"
                              onClick={() => handleModalOpen('edit', manager)}
                            >
                              <PenSquare size={14} />
                              수정
                            </Button>
                            <Button 
                              variant="ghost"
                              size="sm"
                              className="flex items-center gap-1 text-xs text-danger hover:text-red-700"
                              onClick={() => handleDeleteManager(manager.user_id)}
                            >
                              <Trash2 size={14} />
                              삭제
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모바일 카드 리스트 */}
            <div className="flex-1 space-y-3 overflow-y-auto p-4 lg:hidden">
              {managers.map((manager) => (
                <div key={manager.id} className="rounded-3xl bg-gray-50 p-3">
                  <div className="mb-2 flex items-start justify-between">
                    <div>
                      <div className="text-sm font-semibold">ID: {manager.user_id}</div>
                      <div className="text-xs text-gray-600">{manager.user_name}</div>
                    </div>
                  </div>
                  
                  <div className="mb-3 space-y-1 text-xs">
                    <div><span className="font-medium">담당 업무:</span> {manager.charge_work}</div>
                    <div><span className="font-medium">등록일:</span> {new Date(manager.created_at).toLocaleDateString()}</div>
                    <div><span className="font-medium">수정일:</span> {new Date(manager.updated_at).toLocaleDateString()}</div>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 px-2 py-1 text-xs text-primary hover:text-primary-dark"
                      onClick={() => handleModalOpen('edit', manager)}
                    >
                      <PenSquare size={12} />
                      수정
                    </Button>
                    <Button 
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 px-2 py-1 text-xs text-danger hover:text-red-700"
                      onClick={() => handleDeleteManager(manager.user_id)}
                    >
                      <Trash2 size={12} />
                      삭제
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* 페이지네이션 */}
            {!isLoading && managers.length > 0 && (
              <div className="flex justify-center border-t p-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="sm"
                  >
                    이전
                  </Button>
                  {renderPageNumbers()}
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!hasMore}
                    size="sm"
                  >
                    다음
                  </Button>
                </div>
              </div>
            )}

            {!isLoading && managers.length === 0 && (
              <div className="py-8 text-center text-sm text-gray-500 lg:text-base">
                등록된 관리자가 없습니다.
              </div>
            )}
          </div>

          {/* 모달들 */}
          <ManagerModal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onSubmit={modalMode === 'add' ? handleAddManager : handleEditManager}
            initialData={selectedManager}
            mode={modalMode}
          />

          <ConfirmModal
            isOpen={deleteConfirm.isOpen}
            onClose={() => setDeleteConfirm({ isOpen: false, userId: null })}
            onConfirm={confirmDelete}
            title="관리자 삭제"
            message="정말로 이 관리자를 삭제하시겠습니까?"
          />
        </div>
      )}
    />
  );
}

export default ManagerManagement; 